import React, {
  useContext,
  useEffect,
  useState,
  useMemo,
  useCallback,
} from 'react';
import { useSelector } from 'react-redux';
import { Button, Card, Form } from 'react-bootstrap';

import RenderFormItem from 'components/CtxFormFields/FormFieldCtx';
import { Ctx } from 'components/CtxFormFields/CtxInputs';
import useErrorState, { translateError } from 'utils/hooks/useErrorState';
import { getMSDataByEntity } from 'rdx/hardware';
import {
  fieldsToIndexedValues,
  useExtDevList,
} from 'pages/Hardware/Integrations/hooks';
import { integer } from 'components/FieldTypes/formatters';

type ErrorType =
  | string
  | {
      message: string;
      dict: any;
    };

type ErrorsDictType = {
  [key: string]: ErrorType;
};

function ErrorFetchingConfiguration({ retry }: { retry: () => void }) {
  return (
    <Card.Body>
      <Card.Title>Something went wrong</Card.Title>
      <Card.Text>
        Some error has occurred. We were not able to fetch the configuration
        fields.
      </Card.Text>
      <Button variant="primary" onClick={retry}>
        Retry
      </Button>
    </Card.Body>
  );
}

function ExtDevScale() {
  const formContext = useContext(Ctx);
  const { integrationState, setEnabledSave } = formContext.values;

  const ms = useSelector(getMSDataByEntity('extdev-microservice'));
  // @ts-ignore
  const { msIsInstalled } = ms;
  const { list, error, retry } = useExtDevList('scale');

  const [chosen, setChosen] = useState(
    // @ts-ignore
    integrationState?.ScaleDeviceType ?? 'CAS Type-5',
  );

  const fields = useMemo(() => list[chosen], [chosen, list]);
  const showForm = msIsInstalled;

  const onChange = useCallback(
    (key: string, value: string) => {
      // Once the device type is changed,
      if (!key) {
        // set the type to selected one
        setChosen(value);
        // and replace rest with defaults for that type
        formContext.onChange({
          ...fieldsToIndexedValues(list[value]),
          ScaleDeviceType: value,
        });
      } else {
        formContext.onChange({ [key]: value });
      }
    },
    [formContext, list],
  );

  const { errors, hasErrors }: { errors: ErrorsDictType; hasErrors: boolean } =
    useErrorState(integrationState, fieldsToIndexedValues(fields));

  useEffect(() => {
    setEnabledSave(!hasErrors);
  }, [errors]);

  return (
    <Ctx.Provider
      // @ts-ignore
      value={useMemo(() => {
        const values = {
          ...fieldsToIndexedValues(fields),
          ...integrationState,
        };

        return { values, onChange };
      }, [fields, integrationState, onChange])}
    >
      {showForm ? (
        <Card className="flex-fill my-3">
          <Card.Header>Extdev Scale Credentials</Card.Header>
          <Card.Body className="d-flex">
            {error ? (
              <ErrorFetchingConfiguration retry={retry} />
            ) : (
              <Form className="d-flex flex-column flex-fill">
                <RenderFormItem
                  type="select"
                  options={Object.keys(list)}
                  title="Device type"
                  size="md"
                  value={chosen}
                />
                {fields?.map(({ fieldName, title, type, value }) => {
                  const fieldType = (() => {
                    if (type === 'string' || type === 'int') return 'text';
                    if (type === 'bool') return 'checkbox';
                    return type;
                  })();
                  return (
                    <RenderFormItem
                      key={fieldName}
                      size="md"
                      id={fieldName}
                      defaultValue={value}
                      title={title}
                      name={fieldName}
                      type={fieldType}
                      formatter={type === 'int' ? integer : null}
                      error={translateError(errors[fieldName])}
                    />
                  );
                })}
              </Form>
            )}
          </Card.Body>
        </Card>
      ) : (
        <h4>Please make sure that microservice is installed and running</h4>
      )}
    </Ctx.Provider>
  );
}

export default ExtDevScale;
